import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import ProjectDateEntity from '@/entities/projectDate';
import { catchGraphQlError } from '@/lib/catchError';

  import ProjectDateReminderEntity from '@/entities/projectDateReminder';
  import ProjectEntity from '@/entities/project';

function ProjectDateBaseUpdateForm({ formOnly = false, onClose, Entity = ProjectDateEntity, id: propId, ...props }: BaseFormUpdateProps<typeof ProjectDateEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = ProjectDateEntity.model.useGet({
    variables: {
      id,
            withProject: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = ProjectDateEntity.model.useUpdate();

  const relationsOptionsQuery = ProjectDateEntity.model.useRelationsOptions({
    variables: {
          withProjects: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              ProjectEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:projectDate') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateProjectDate);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.projectDate}
    />
  );
}

export default ProjectDateBaseUpdateForm;
